/**
 * AgendaList — chronological list grouped by day.
 *
 * Each group is one day with a header (formatted date + relative day count
 * like "Hoy", "Mañana", "en 3 días") followed by event rows.
 *
 * Props:
 *   - events: array sorted by date ASC (already filtered)
 *   - todayISO: ISO date for relative labels
 *   - onEventClick(event): callback (opens detail panel)
 */
import React, { useMemo } from "react";
import EventBadge from "@/components/calendar/EventBadge";
import { CaretRight } from "@phosphor-icons/react";

function formatDay(iso, todayISO) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(todayISO + "T00:00:00");
  const ms = d.getTime() - today.getTime();
  const days = Math.round(ms / 86400000);
  let rel = "";
  if (days === 0) rel = "Hoy";
  else if (days === 1) rel = "Mañana";
  else if (days === -1) rel = "Ayer";
  else if (days > 0) rel = `en ${days} días`;
  else rel = `hace ${Math.abs(days)} días`;
  const formatted = d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
  return { formatted, rel, days };
}

function fmtAmount(n) {
  if (!n && n !== 0) return null;
  return `€${Number(n).toLocaleString("es-ES")}`;
}

export default function AgendaList({ events, todayISO, onEventClick }) {
  const groups = useMemo(() => {
    const map = {};
    for (const e of events) {
      (map[e.date] = map[e.date] || []).push(e);
    }
    const dates = Object.keys(map).sort();
    return dates.map(dt => ({ date: dt, items: map[dt] }));
  }, [events]);

  if (groups.length === 0) {
    return (
      <div className="dv-card text-center py-16 text-stone-500" data-testid="agenda-empty">
        <div className="font-serif-dv text-2xl mb-1">Sin eventos</div>
        <div className="text-sm">Ningún evento en este rango con los filtros activos.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agenda-list">
      {groups.map(g => {
        const { formatted, rel, days } = formatDay(g.date, todayISO);
        const isPast = days < 0;
        return (
          <div key={g.date}>
            <div className="flex items-baseline justify-between mb-2 pb-2 border-b border-stone-200">
              <div>
                <div className="font-serif-dv text-lg capitalize">{formatted}</div>
              </div>
              <div className={`text-xs uppercase tracking-widest ${days === 0 ? "text-[#9C5236]" : isPast ? "text-stone-400" : "text-stone-500"}`}>
                {rel}
              </div>
            </div>
            <ul className="space-y-1.5">
              {g.items.map(ev => (
                <li key={ev.event_id}>
                  <button
                    onClick={() => onEventClick(ev)}
                    data-testid={`agenda-row-${ev.event_id}`}
                    className="w-full text-left flex items-center gap-3 bg-white border border-stone-200 rounded px-3 py-2.5 hover:border-stone-400 transition-colors"
                  >
                    <EventBadge type={ev.type} severity={ev.severity} size="xs">
                      {ev.type === "payment" ? "Pago"
                        : ev.type === "task" ? "Tarea"
                        : ev.type === "milestone" ? "Hito"
                        : ev.type === "meeting" ? "Reunión"
                        : "Entrega"}
                    </EventBadge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-900 truncate">{ev.title}</div>
                      {ev.subtitle && (
                        <div className="text-xs text-stone-500 truncate">{ev.subtitle}</div>
                      )}
                    </div>
                    {ev.amount_eur && (
                      <div className="text-sm font-medium text-stone-900 whitespace-nowrap">
                        {fmtAmount(ev.amount_eur)}
                      </div>
                    )}
                    {ev.status && (
                      <StatusChip status={ev.status} />
                    )}
                    <CaretRight size={14} weight="bold" className="text-stone-400" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function StatusChip({ status }) {
  const STYLE = {
    overdue:  "bg-red-100 text-red-700",
    pending:  "bg-amber-100 text-amber-800",
    paid:     "bg-emerald-100 text-emerald-800",
    todo:     "bg-stone-100 text-stone-700",
    in_progress: "bg-blue-100 text-blue-800",
    done:     "bg-stone-100 text-stone-500",
  };
  const LABEL = {
    overdue:  "Vencido",
    pending:  "Pendiente",
    paid:     "Pagado",
    todo:     "Por hacer",
    in_progress: "En curso",
    done:     "Hecho",
  };
  return (
    <span className={`hidden sm:inline-flex text-[11px] uppercase tracking-wider px-2 py-0.5 rounded ${STYLE[status] || "bg-stone-100 text-stone-700"}`}>
      {LABEL[status] || status}
    </span>
  );
}
